import { DESTINATION_OPTIONS } from '@/data/destinationOptions';
import type { Trip } from '@/services/trips';
import { fromIso2CountryCode, toIso2CountryCode } from '@/utils/countryIso2';

function normalizeCityKey(input: string): string {
  return input.toLowerCase().trim().replace(/[^a-z0-9]+/g, '');
}

function isoFromLocationText(text: string): string | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;

  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const iso = toIso2CountryCode(parts[i]);
      if (iso) return iso;
    }
  }

  return toIso2CountryCode(trimmed);
}

const CITY_TO_COUNTRY_ISO2: Record<string, string> = (() => {
  const index: Record<string, string> = {};
  for (const option of DESTINATION_OPTIONS) {
    if (!option.label.includes(',')) continue;
    const parts = option.label.split(',').map((part) => part.trim()).filter(Boolean);
    const cityKey = normalizeCityKey(parts[0] ?? '');
    const countryIso = isoFromLocationText(parts[parts.length - 1] ?? '');
    if (cityKey && countryIso) {
      index[cityKey] = countryIso;
    }
  }
  return index;
})();

/** Resolve ISO2 destination country from structured trip fields and legacy strings. */
export function resolveTripDestinationCountryIso2(
  trip: Pick<Trip, 'destination' | 'destinations'> | null | undefined,
): string | undefined {
  if (!trip) return undefined;

  const dest0 = trip.destinations?.[0];
  if (dest0?.countryCode) {
    const fromCode = toIso2CountryCode(String(dest0.countryCode));
    if (fromCode) return fromCode;
  }
  if (dest0?.country) {
    const fromCountry = toIso2CountryCode(String(dest0.country));
    if (fromCountry) return fromCountry;
  }

  const candidates = [trip.destination, dest0?.city].filter(Boolean).map(String);
  for (const raw of candidates) {
    const fromText = isoFromLocationText(raw);
    if (fromText) return fromText;

    const cityKey = normalizeCityKey(raw.split(',')[0] ?? raw);
    const fromCity = CITY_TO_COUNTRY_ISO2[cityKey];
    if (fromCity) return fromCity;
  }

  return undefined;
}

export function tripNeedsDestinationCountryConfirm(
  trip: Pick<Trip, 'destination' | 'destinations'> | null | undefined,
): boolean {
  if (!trip) return false;
  const raw = (trip.destination || trip.destinations?.[0]?.city || '').toString().trim();
  if (!raw) return true;
  return !resolveTripDestinationCountryIso2(trip);
}

/** Human-readable destination country for visa UI and messaging. */
export function getTripDestinationCountryLabel(
  trip: Pick<Trip, 'destination' | 'destinations'> | null | undefined,
): string | undefined {
  const iso2 = resolveTripDestinationCountryIso2(trip);
  if (iso2) {
    return fromIso2CountryCode(iso2) || iso2;
  }

  const raw = (trip?.destination || trip?.destinations?.[0]?.city || '').toString().trim();
  if (!raw) return undefined;
  if (raw.includes(',')) {
    return raw.split(',').map((part) => part.trim()).filter(Boolean).pop();
  }
  return raw;
}

/** Split user/location text into city + country (handles "City, State, Country"). */
export function parseCityAndCountryFromText(
  cityText: string,
  countryHint?: string,
): { city: string; country?: string } {
  const trimmed = cityText.trim();
  let city = trimmed;
  let country = countryHint?.trim();

  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      city = parts[0] ?? trimmed;
      if (!country) {
        country = parts[parts.length - 1];
      }
    }
  }

  return { city, country: country || undefined };
}
